import { config } from "./config";
import type { AuthState } from "./auth/AuthProvider";
import { markSessionExpired, performSignOut } from "./auth/performSignOut";
import { ApiError } from "./apiError";
import { captureApiFailure } from "./sentry";

// Shapes the customer screens already render. A vehicle write names the business.
export interface SavedVehicle {
  registration: string;
  make: string | null;
  model: string | null;
  colour: string | null;
  size: string;
}

export interface CustomerAccount {
  customerId: string;
  status: string;
  phoneNumber: string;
  platformConsentVersion: string;
  vehicles: SavedVehicle[];
  marketingConsents: { businessId: string; consentVersion: string; grantedAt: string }[];
}

export interface CustomerBusinesses {
  businesses: {
    businessId: string;
    businessName: string;
    visitCount: number | string;
    totalSpend: number | string;
    firstVisitAt: string | null;
    lastVisitAt: string | null;
    lastVisitServices: { name: string; price?: number | string; code?: string }[];
    marketingConsent: {
      granted: boolean;
      consentVersion: string | null;
      grantedAt: string | null;
    } | null;
  }[];
}

export interface CustomerVisits {
  totalCount: number | string;
  page: number | string;
  pageSize: number | string;
  items: {
    visitId: string;
    businessId: string;
    businessName: string;
    siteId: string;
    siteName: string;
    capturedAt: string;
    status: string;
    services: { name: string; price?: number | string }[];
    quotedPrice: number | string;
    trackingToken: string;
  }[];
}

export interface ConsentTexts {
  platform: { version: string; text: string };
  businessMarketing: { version: string; text: string };
}

export { ApiError };

type FetchOpts = { method?: "GET" | "POST" | "DELETE"; body?: unknown; auth?: AuthState };

async function once<T>(path: string, opts: FetchOpts, token: string | null): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${config.apiBase}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    let retryAfterSeconds: number | undefined;
    try {
      const problem = await res.json();
      if (problem?.detail) detail = problem.detail;
      if (typeof problem?.retryAfterSeconds === "number") retryAfterSeconds = problem.retryAfterSeconds;
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, detail, retryAfterSeconds);
  }
  if (res.status === 204 || res.status === 202) return undefined as T;
  return (await res.json()) as T;
}

export async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const token = opts.auth ? await opts.auth.getIdToken() : null;
  try {
    return await once<T>(path, opts, token);
  } catch (e) {
    // One forced-refresh retry on 401; a second 401 means the session is dead.
    if (e instanceof ApiError && e.status === 401 && opts.auth) {
      const fresh = await opts.auth.getIdToken(true);
      try {
        return await once<T>(path, opts, fresh);
      } catch (e2) {
        if (e2 instanceof ApiError && e2.status === 401) {
          // No QueryClient reference here (see performSignOut's doc comment) — this still
          // gets sign-out + Sentry hygiene, and flags the reason so /login can explain why
          // the user landed back there (spec §6).
          markSessionExpired();
          await performSignOut(opts.auth);
        }
        captureApiFailure(e2, { path });
        throw e2;
      }
    }
    captureApiFailure(e, { path });
    throw e;
  }
}

export const api = {
  requestOtp: (body: { phoneNumber: string; clientCommandId: string; turnstileToken: string | null }) =>
    apiFetch<void>("/auth/otp/request", { method: "POST", body }),
  verifyOtp: (body: { phoneNumber: string; code: string; clientCommandId: string }) =>
    apiFetch<{ customToken: string }>("/auth/otp/verify", { method: "POST", body }),
  consentTexts: () => apiFetch<ConsentTexts>("/consent-texts"),
  me: (auth: AuthState) => apiFetch<CustomerAccount>("/customers/me", { auth }),
  register: (auth: AuthState, body: { clientCommandId: string; acceptedConsentVersion: string }) =>
    apiFetch<CustomerAccount>("/customers/register", { method: "POST", body, auth }),
  businesses: (auth: AuthState) => apiFetch<CustomerBusinesses>("/customers/me/businesses", { auth }),
  visits: (auth: AuthState, page: number) =>
    apiFetch<CustomerVisits>(`/customers/me/visits?page=${page}`, { auth }),
  addVehicle: (
    auth: AuthState,
    body: {
      businessId: string;
      subjectKind: "vehicle";
      attributes: {
        registration: string;
        make: string;
        model: string;
        colour: string;
        size: string;
      };
      clientCommandId: string;
    },
  ) => apiFetch<CustomerAccount>("/customers/me/vehicles", { method: "POST", body, auth }),
  // No ClientCommandId: RemoveVehicleRequest carries only `registration` (schema.d.ts) — the
  // backend endpoint is deliberately idempotent-by-construction (a no-op 200 when the vehicle
  // isn't there), so replay protection isn't needed. See RemoveVehicleEndpoint.cs.
  removeVehicle: (auth: AuthState, body: { registration: string }) =>
    apiFetch<CustomerAccount>("/customers/me/vehicles/remove", { method: "POST", body, auth }),
  grantConsent: (
    auth: AuthState,
    body: { businessId: string; acceptedConsentVersion: string; clientCommandId: string },
  ) => apiFetch<CustomerAccount>("/customers/me/marketing-consent", { method: "POST", body, auth }),
  // No ClientCommandId, anywhere (not query, not body): DELETE_customers_me_marketing_consent_
  // targetBusinessId has no requestBody and no query parameters in schema.d.ts — only the
  // {targetBusinessId} path segment. RevokeMarketingConsentEndpoint.cs is explicit about why:
  // revocation is idempotent by construction (a no-op 200 when there's nothing to revoke), so
  // it deliberately takes no replay-protection key.
  revokeConsent: (auth: AuthState, businessId: string) =>
    apiFetch<CustomerAccount>(`/customers/me/marketing-consent/${businessId}`, { method: "DELETE", auth }),
};
